import React, { memo } from 'react';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';

import DataFieldCDS from '../DataFieldCDS';
import FieldWrapper from '../FieldWrapper';
import HelperText from '../HelperText';
import Label from '../Label';

import { CombinedFieldSize } from './CombinedFieldCDS.constants';
import { fieldRowStyles, getFieldStyles, getGroupStyles } from './CombinedFieldCDS.styles';
import { getFieldGateProps, getGroupHelper } from './CombinedFieldCDS.utils';

const CombinedFieldCDS = ({
  fields,
  label = '',
  helperText = '',
  required = false,
  withRequiredIcon = true,
  error = false,
  disabled = false,
  isHidden = false,
  isFieldChanged = false,
  noValidate = false,
  columns = 1,
  width = '100%',
  size = CombinedFieldSize.LARGE,
  customStyles,
  dataTestId,
}) => {
  const groupHelper = getGroupHelper({ helperText, error, fields });

  return (
    <FieldWrapper
      columns={columns}
      customStyles={getGroupStyles({ customStyles })}
      isHidden={isHidden}
      width={width}
    >
      {label && (
        <Label name={fields[0]?.name} required={required} withRequiredIcon={withRequiredIcon}>
          {label}
        </Label>
      )}

      <Box data-testid={dataTestId || null} sx={fieldRowStyles}>
        {fields.map(
          (
            {
              key,
              widthPercent,
              customStyles: fieldCustomStyles,
              helperText: fieldHelperText,
              ...fieldProps
            },
            index,
          ) => (
            <DataFieldCDS
              key={key ?? fieldProps.name}
              size={size}
              {...fieldProps}
              {...getFieldGateProps({
                group: { disabled, error, isFieldChanged, noValidate },
                field: fieldProps,
              })}
              customStyles={getFieldStyles({
                widthPercent,
                isFirst: !index,
                isLast: index === fields.length - 1,
                customStyles: fieldCustomStyles,
              })}
            />
          ),
        )}
      </Box>

      {groupHelper.helperText && (
        <HelperText error={groupHelper.error}>{groupHelper.helperText}</HelperText>
      )}
    </FieldWrapper>
  );
};

CombinedFieldCDS.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      key: PropTypes.string,
      widthPercent: PropTypes.number,
      dataTestId: PropTypes.string,
      helperText: PropTypes.string,
      customStyles: PropTypes.object,
    }),
  ).isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  helperText: PropTypes.string,
  required: PropTypes.bool,
  withRequiredIcon: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  noValidate: PropTypes.bool,
  columns: PropTypes.number,
  width: PropTypes.string,
  size: PropTypes.oneOf(Object.values(CombinedFieldSize)),
  customStyles: PropTypes.object,
  dataTestId: PropTypes.string,
};

export default memo(CombinedFieldCDS);
